import type { AutomationSettings } from "@repo/app-types/entities/automation";
import isEqual from "lodash-es/isEqual.js";

/** A snapshot of the editable automation config: its name and settings. */
export interface AutomationConfigSnapshot {
  name: string;
  settings: AutomationSettings;
}

/**
 * Whether an in-progress config `draft` differs from the `saved` automation,
 * used to drive autosave.
 *
 * Settings are compared with a deep, key-order-insensitive equality (lodash
 * `isEqual`) rather than `JSON.stringify`. The server persists settings in a
 * `jsonb` column, which normalizes object key order (Postgres sorts keys by
 * length, then bytewise), so the settings that come back from a save rarely
 * match the key order of the draft the client just sent. A stringify comparison
 * would then read an unchanged draft as different from the saved value — most
 * visibly after changing a custom agent's schedule frequency, which rebuilds
 * the trigger object in its own literal key order (e.g. `{ frequency, weekdays,
 * time_of_day }`, which `jsonb` returns as `{ weekdays, frequency, time_of_day
 * }`). That divergence would leave the config permanently "dirty", so autosave
 * would re-PATCH on every debounce forever.
 *
 * Array order (e.g. the weekly weekday list) stays significant, which is what
 * we want: the UI keeps it deterministic, so a genuine reordering is a real
 * change worth saving.
 */
export function checkIsAutomationConfigDirty(
  draft: AutomationConfigSnapshot,
  saved: AutomationConfigSnapshot,
): boolean {
  return draft.name !== saved.name || !isEqual(draft.settings, saved.settings);
}
